export class MarkdownStreamParser {
    private container: HTMLElement;
    private generatedHtmlStrings: string[] = [];
    private stack: HTMLElement[] = []; // The stack of open DOM elements.
    private buffer = '';               // A buffer for the incoming stream.
    private isNewLine = true;          // State to track if we're at the beginning of a line.
    private isInTable = false;         // State to track if we are inside a table block.
    private isParsingTableContent = false; // New flag to distinguish between header and content parsing.
    private streamEnded = false;
    private pendingAutolink: { el: HTMLAnchorElement; text: string } | null = null;

    constructor(container: HTMLElement) {
        this.container = container;
    }

    feed(markdownChunk: string): void {
        this.buffer += markdownChunk;
        this.parse();
    }
    clear(){
        this.buffer="";
        this.complete();
    }

    /**
     * If the buffer ends with a whitespace-delimited token that looks like the
     * beginning of a plain URL or email (but isn't yet a complete autolink),
     * returns its start index so the caller can hold it until more data arrives.
     * Returns -1 otherwise. Markdown links are handled separately by
     * findIncompleteLinkStart, so this only sees bare URLs/emails.
     */
    private findTrailingPartialAutolink(text: string): number {
        const m = text.match(/(\S+)$/);
        if (!m) return -1;
        const token = m[1];
        if (/^https?:\/\//.test(token) || /^[^\s`@]+@[^\s`]*$/.test(token)) {
            return m.index!;
        }
        return -1;
    }

    complete(): void {
        this.streamEnded = true;
        this.parse(); // flush/finalize any held URL now that no more data is coming

        if (this.isInTable) {
            this.closeTable();
        }
        let current = this.getCurrentElement();
        while (current && (current.tagName === 'UL' || current.tagName === 'OL' || current.tagName === 'LI')) {
            this.popElement();
            current = this.getCurrentElement();
        }
        if (this.buffer) {
            this.appendText(this.buffer);
            this.buffer = '';
        }
        this.stack = [];
        this.pendingAutolink = null;
        this.streamEnded = false; // reset so the instance can be reused
        this.generatedHtmlStrings = Array.from(this.container.children).map(child => child.outerHTML);
    }

    getGeneratedHtmlStrings(): string[] {
        return this.generatedHtmlStrings;
    }

    private parse(): void {
        const tokenRegex = /(\*\*\*|\*\*|\*|`|~~|\n|\[.*?\]\(.*?\)|https?:\/\/[^\s`]+\.[^\s`]+|\S+@[^\s`]+\.[^\s`]+)/g;

        while (this.buffer.length > 0) {
            // If a link is pending from the previous chunk, the buffer may begin with more
            // of that same URL (no whitespace in between). Grow the existing <a> rather
            // than spilling the continuation into a separate text node.
            if (this.pendingAutolink) {
                const m = this.buffer.match(/^[^\s`]+/);
                if (m) {
                    const run = m[0];
                    const runReachesEnd = run.length === this.buffer.length;
                    this.extendAutolink(run);
                    this.buffer = this.buffer.substring(run.length);
                    if (runReachesEnd && !this.streamEnded) break; // may grow further
                    this.pendingAutolink = null;
                    if (runReachesEnd) break; // stream ended, nothing left
                    continue;
                }
                this.pendingAutolink = null; // buffer starts with a terminator; link is final
            }
            if (this.isNewLine) {
                const blockStatus = this.tryParseBlock();
                if (blockStatus === 'parsed') continue;
                if (blockStatus === 'waiting') break;
                if (this.buffer.trim().length > 0) this.isNewLine = false;
            }

            if (this.isParsingTableContent) {
                const tableContentStatus = this.parseTableContent();
                if (tableContentStatus === 'parsed') continue;
                if (tableContentStatus === 'waiting') break;
            }

            tokenRegex.lastIndex = 0;
            const match = tokenRegex.exec(this.buffer);

            // If a markdown link/image is only partially in the buffer (its closing
            // ')' or ']' hasn't streamed in yet), flush any text preceding it and
            // wait for the rest before parsing. This stops the autolink alternative
            // from grabbing a half-finished URL out of "[text](http..."
            const incompleteLinkStart = this.findIncompleteLinkStart(this.buffer);
            if (!this.streamEnded && incompleteLinkStart !== -1 && (!match || match.index >= incompleteLinkStart)) {
                const textBefore = this.buffer.substring(0, incompleteLinkStart);
                if (textBefore) {
                    this.appendText(textBefore);
                    this.buffer = this.buffer.substring(incompleteLinkStart);
                }
                break; // wait for more data to complete the link
            }

            if (!match) {
                // No token found, but the tail might be the *start* of a plain URL/email
                // whose remainder hasn't streamed in yet (e.g. "http://exa" with no dot).
                // Hold it back until a terminator arrives.
                if (!this.streamEnded) {
                    const partialStart = this.findTrailingPartialAutolink(this.buffer);
                    if (partialStart !== -1) {
                        const before = this.buffer.substring(0, partialStart);
                        if (before) this.appendText(before);
                        this.buffer = this.buffer.substring(partialStart);
                        break; // wait for the rest of the URL
                    }
                }
                this.appendText(this.buffer);
                this.buffer = '';
                break;
            }

            const token = match[0];
            const textBefore = this.buffer.substring(0, match.index);
            const isAutolinkToken = /^(https?:\/\/[^\s`]+\.[^\s`]+|\S+@[^\s`]+\.[^\s`]+)$/.test(token);
            const reachesEnd = match.index + token.length === this.buffer.length;

            // A plain URL/email running to the end of the buffer may still be growing in
            // the next chunk. Render it as a link NOW (so it shows live) and remember it
            // so a continuation can extend it in place instead of holding it invisibly.
            if (!this.streamEnded && isAutolinkToken && reachesEnd) {
                if (textBefore) this.appendText(textBefore);
                this.appendAutolink(token);
                this.buffer = '';
                break;
            }

            if (textBefore) this.appendText(textBefore);
            this.handleInlineToken(token);
            this.buffer = this.buffer.substring(match.index + token.length);
        }
    }

    private appendAutolink(url: string): void {
        const href = url.startsWith('http') ? url : `mailto:${url}`;
        this.appendText(url, true, href); // reuses block-ensuring + escaping logic
        const el = this.getCurrentElement()?.lastElementChild as HTMLAnchorElement | null;
        if (el && el.tagName === 'A') this.pendingAutolink = { el, text: url };
    }

    private extendAutolink(more: string): void {
        if (!this.pendingAutolink) return;
        this.pendingAutolink.text += more;
        const { el, text } = this.pendingAutolink;
        el.textContent = text;
        el.setAttribute('href', text.startsWith('http') ? text : `mailto:${text}`);
    }

    /**
     * Returns the index of the first '[' (or the '!' of an image) that begins a
     * markdown link/image which has NOT fully arrived yet, or -1 if none.
     * Complete links earlier in the buffer are skipped so they parse normally,
     * and a '[' that clearly isn't a link (followed by something other than '(',
     * or broken by a newline) is skipped so we never stall on stray brackets.
     */
    private findIncompleteLinkStart(text: string): number {
        let searchFrom = 0;
        while (searchFrom < text.length) {
            const bracket = text.indexOf('[', searchFrom);
            if (bracket === -1) return -1;

            // FIX: If the bracket is embedded inside a literal URL, skip it
            // so it doesn't falsely trip the incomplete markdown link lookahead.
            const textBeforeBracket = text.substring(0, bracket);
            if (/https?:\/\/[^\s`]+$/.test(textBeforeBracket)) {
                searchFrom = bracket + 1;
                continue;
            }

            const start = (bracket > 0 && text[bracket - 1] === '!') ? bracket - 1 : bracket;
            const rest = text.substring(bracket);

            const closeBracket = rest.indexOf(']');
            if (closeBracket === -1) {
                // No ']' yet. A newline means it can't be a single-line link.
                return rest.indexOf('\n') === -1 ? start : -1;
            }

            const afterClose = rest.charAt(closeBracket + 1);
            if (afterClose === '') {
                // ']' is the last char so far; next char might be '(' -> wait.
                return start;
            }
            if (afterClose !== '(') {
                // '[...]' not followed by '(' -> not a link, keep scanning.
                searchFrom = bracket + closeBracket + 1;
                continue;
            }

            // Inside the URL portion '[...](...'; need the closing ')'.
            const closeParen = rest.indexOf(')', closeBracket + 2);
            if (closeParen === -1) {
                return rest.indexOf('\n', closeBracket + 1) === -1 ? start : -1;
            }

            // Complete link found; skip past it and keep looking.
            searchFrom = bracket + closeParen + 1;
        }
        return -1;
    }
    private handleInlineToken(token: string): void {
        const currentElement = this.getCurrentElement();
        const parentElement = this.stack.length > 1 ? this.stack[this.stack.length - 2] : null;

        // Autolink and standard link handling
        const autolinkMatch = token.match(/^(https?:\/\/[^\s`]+\.[^\s`]+|\S+@[^\s`]+\.[^\s`]+)$/);
        const linkMatch = token.match(/\[(.*?)\]\((.*?)\)/);
        if (linkMatch) {
            this.appendText(linkMatch[1], true, linkMatch[2]);
            return;
        } else if (autolinkMatch) {
            const href = autolinkMatch[0].startsWith('http') ? autolinkMatch[0] : `mailto:${autolinkMatch[0]}`;
            this.appendText(autolinkMatch[0], true, href);
            return;
        }

        switch (token) {
            case '`':
                if (currentElement?.tagName === 'CODE') { this.popElement(); } else { this.pushElement('code'); }
                break;
            case '***':
                if (currentElement?.tagName === 'I' && parentElement?.tagName === 'B') {
                    this.popElement(); this.popElement();
                } else {
                    this.pushElement('b'); this.pushElement('i');
                }
                break;
            case '**':
                if (currentElement?.tagName === 'B') { this.popElement(); } else { this.pushElement('b'); }
                break;
            case '*':
                if (currentElement?.tagName === 'I') { this.popElement(); } else { this.pushElement('i'); }
                break;
            case '~~':
                if (currentElement?.tagName === 'DEL') { this.popElement(); } else { this.pushElement('del'); }
                break;
            case '\n':
                this.closeCurrentBlock(false);
                this.isNewLine = true;
                break;
        }
    }

    private tryParseBlock(): 'parsed' | 'waiting' | 'failed' {
        const markdown = this.buffer.trimStart();

        // Check for a non-list line following a list.
        const isNextLineAListItem = markdown.startsWith('-') || markdown.startsWith('*') || markdown.startsWith('+') || markdown.match(/^\d+\./) || markdown.match(/^(\s{2,}|\t)([-*]|\d+\.)/);
        const isCurrentElementList = this.getCurrentElement()?.tagName === 'UL' || this.getCurrentElement()?.tagName === 'OL';

        // console.log("tryParseBlock", this.buffer, this.getCurrentElement()?.tagName, isCurrentElementList, isNextLineAListItem);
        // console.log(this.stack);

        if (isCurrentElementList && !isNextLineAListItem) {
            // Close the entire list block if the new line is not a list item.
            while (this.stack.length > 0 && (this.getCurrentElement()!.tagName === 'UL' || this.getCurrentElement()!.tagName === 'OL' || this.getCurrentElement()!.tagName === 'LI')) {
                this.popElement();
            }
        }

        // Table parsing
        if (markdown.startsWith('|')) {
            if (!this.isInTable) {
                this.closeCurrentBlock(true);
                this.isInTable = true;
                this.isParsingTableContent = true;
                this.pushElement('table');
                this.pushElement('tbody');
            }
            const tableContentStatus = this.parseTableContent();
            if (tableContentStatus !== 'failed') {
                return tableContentStatus;
            }
        }

        if (this.isInTable) {
            this.closeTable();
        }

        // Blockquote parsing
        if (markdown.startsWith('>')) {
            this.closeCurrentBlock(false);
            this.pushElement('blockquote');
            this.buffer = this.buffer.substring(this.buffer.indexOf('>') + 1);
            this.isNewLine = false;
            return 'parsed';
        }

        // Fenced code block
        if (markdown.startsWith('```')) {
            const closingDelimiterIndex = this.buffer.indexOf('```', 3);
            if (closingDelimiterIndex !== -1) {
                this.closeCurrentBlock(false);
                const codeContent = this.buffer.substring(3, closingDelimiterIndex).trim();
                const pre = document.createElement('pre');
                const code = document.createElement('code');
                code.textContent = codeContent;
                pre.appendChild(code);
                (this.getCurrentElement() || this.container).appendChild(pre);
                this.buffer = this.buffer.substring(closingDelimiterIndex + 3);
                this.isNewLine = true;
                return 'parsed';
            }
            return 'waiting';
        }

        // Headings
        const headingMatch = markdown.match(/^(#+)\s/);
        if (headingMatch) {
            this.closeCurrentBlock(true);
            this.pushElement(`h${headingMatch[1].length}` as keyof HTMLElementTagNameMap);
            this.buffer = this.buffer.substring(this.buffer.indexOf(headingMatch[0]) + headingMatch[0].length);
            this.isNewLine = false;
            return 'parsed';
        }

        // Horizontal rules
        const hrMatch = markdown.substring(0, 3).match(/^(---|___|\*\*\*)\s*$/);
        if (hrMatch) {
            this.closeCurrentBlock(true);
            const hr = document.createElement('hr');
            (this.getCurrentElement() || this.container).appendChild(hr);
            this.buffer = this.buffer.substring(this.buffer.indexOf(hrMatch[0]) + hrMatch[0].length);
            return 'parsed';
        }
        const hrMatch2 = markdown.substring(0, 5).match(/^(- - -)\s*$/);
        if (hrMatch2) {
            this.closeCurrentBlock(true);
            const hr = document.createElement('hr');
            (this.getCurrentElement() || this.container).appendChild(hr);
            this.buffer = this.buffer.substring(this.buffer.indexOf(hrMatch2[0]) + hrMatch2[0].length);
            return 'parsed';
        }

        // List items (including nested lists and task lists)
        const listMatch = markdown.match(/^((\s{2,}|\t)?)(([-*]|\d+\.)\s)/);
        const taskListMatch = markdown.match(/^((\s{2,}|\t)?)(([-*]|\d+\.)\s\[([x ])\]\s)/);

        if (taskListMatch || listMatch) {
            // Determine the indentation level
            // const indentLevel = (taskListMatch || listMatch)![1]?.length || 0;
            // console.log("List item detected with indent level:", indentLevel);
            const indentLevel=Math.floor(this.buffer.split(markdown.substring(0,1))[0].replace("    ", "    ").split("").length/4);

            // Handle list item closure and nesting
            let currentDepth = 0;
            let tempElement = this.getCurrentElement();
            while (tempElement && tempElement.tagName === 'LI') {
                tempElement = this.stack[this.stack.indexOf(tempElement) - 1];
                if (tempElement?.tagName === 'UL' || tempElement?.tagName === 'OL') {
                    currentDepth++;
                }
            }
            // console.log("indentCheck", indentLevel, "currentDepth", currentDepth);

            // This is a simplified check, a more robust solution would track indentation explicitly.
            // For now, we'll pop the previous LI to ensure a new one is created.
            if (this.getCurrentElement()?.tagName === 'LI') {
                this.popElement();
            }

            if (taskListMatch) {
                this.ensureList('ul', indentLevel);
                console.log("create task list item", this.buffer, "taskListMatch", taskListMatch);
                const isChecked = taskListMatch[5] === 'x';
                this.pushElement('li');
                const li = this.getCurrentElement()!;
                li.className = 'task-list-item';
                const checkbox = document.createElement('input');
                checkbox.name="taskItem_"+new Date().getTime()+"_"+Math.round(100000*Math.random());
                checkbox.type = 'checkbox';
                checkbox.checked = isChecked;
                checkbox.style.pointerEvents="none";
                checkbox.tabIndex=-1;
                // checkbox.disabled = true;
                li.appendChild(checkbox);
                this.buffer = this.buffer.substring(this.buffer.indexOf(taskListMatch[0]) + taskListMatch[0].length);
                this.isNewLine = false;
                return 'parsed';
            } else if (listMatch) {
                const listType = listMatch[3].endsWith('.') ? 'ol' : 'ul';
                this.ensureList(listType, indentLevel);
                // console.log("ensureList", listType);
                // console.log("create list item", this.buffer);
                this.pushElement('li');
                this.buffer = this.buffer.substring(this.buffer.indexOf(listMatch[0]) + listMatch[0].length);
                this.isNewLine = false;
                return 'parsed';
            }
        }

        // Standard paragraph break
        if (markdown.startsWith('\n')) {
            this.closeCurrentBlock(false);
            this.buffer = this.buffer.substring(this.buffer.indexOf('\n') + 1);
            return 'parsed';
        }

        return 'failed';
    }

    private parseTableContent(): 'parsed' | 'waiting' | 'failed' {
        const newLineIndex = this.buffer.indexOf('\n');
        if (newLineIndex === -1) {
            return 'waiting';
        }

        const line = this.buffer.substring(0, newLineIndex).trim();

        if (line.startsWith('|')) {
            const separatorMatch = line.match(/^\|(?:\s*:?-+:?\s*\|)+$/);
            if (separatorMatch) {
                this.buffer = this.buffer.substring(newLineIndex + 1);
                return 'parsed';
            } else {
                this.parseTableRow(line, 'td');
                this.buffer = this.buffer.substring(newLineIndex + 1);
                return 'parsed';
            }
        } else {
            this.closeTable();
            return 'failed';
        }
    }

    private parseTableRow(line: string, cellType: 'th' | 'td'): void {
        this.pushElement('tr');
        const cells = line.split('|').map(s => s.trim()).filter(s => s.length > 0);
        cells.forEach(cellContent => {
            const cellElement = document.createElement(cellType);
            this.pushElement(cellType);
            this.appendText(cellContent);
            this.popElement();
        });
        this.popElement(); // Pop the tr
    }

    private getCurrentElement(): HTMLElement | null {
        return this.stack.length > 0 ? this.stack[this.stack.length - 1] : null;
    }

    private pushElement(tag: keyof HTMLElementTagNameMap): void {
        const parent = this.getCurrentElement() || this.container;
        const newElement = document.createElement(tag);
        parent.appendChild(newElement);
        this.stack.push(newElement);
    }

    private popElement(): void {
        if(this.stack.length > 0) {
            this.stack.pop();
        }
    }

    private static safeUrl(raw: string, forImage = false): string | null {
        const u = (raw || '').replace(/[\u0000-\u001f\u007f\s]+/g, '');
        if (!u) return null;
        if (u.startsWith('//')) return null;
        if (u.startsWith('/')) return u;
        if (!forImage && u.startsWith('#')) return u;
        const m = u.match(/^([a-zA-Z][a-zA-Z0-9+.\-]*):/);
        if (!m) return forImage ? null : u;
        const scheme = m[1].toLowerCase();
        const allowed = forImage ? ['http', 'https'] : ['http', 'https', 'mailto', 'tel'];
        return allowed.includes(scheme) ? u : null;
    }

    private appendText(text: string, isLink = false, href = ''): void {
        if (!text) return;

        // text = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        // href = href.replace(/</g, '&lt;').replace(/>/g, '&gt;');

        let current = this.getCurrentElement();
        // Check if the current element is a valid block for appending text.
        // We remove BLOCKQUOTE from the list to allow text to be directly appended to it.
        if (!current || ['UL', 'OL', 'TABLE', 'THEAD', 'TBODY'].includes(current.tagName) || current === this.container) {
            this.closeCurrentBlock(false);
            this.pushElement('p');
            current = this.getCurrentElement();
        }

        const imageMatch = text.match(/^!\[(.*?)\]\((.*?)\)$/);
        if (imageMatch) {
            const safeSrc = MarkdownStreamParser.safeUrl(imageMatch[2], true);
            if (safeSrc !== null) {
                const img = document.createElement('img');
                img.alt = imageMatch[1];
                img.src = safeSrc;
                current!.appendChild(img);
            } else if (imageMatch[1]) {
                current!.appendChild(document.createTextNode(imageMatch[1]));
            }
        } else if (isLink) {
            const safeHref = MarkdownStreamParser.safeUrl(href);
            if (safeHref !== null) {
                const link = document.createElement('a');
                link.href = safeHref;
                link.target = "_blank";
                link.rel = "noopener noreferrer";
                link.appendChild(document.createTextNode(text));
                current!.appendChild(link);
            } else {
                current!.appendChild(document.createTextNode(text));
            }
        } else {
            current!.appendChild(document.createTextNode(text));
        }
    }

    private closeCurrentBlock(closeAllBlocks:boolean): void {
        if (this.isInTable) return;
        const current = this.getCurrentElement();

        // console.log("close current block", current?.tagName, this.stack);
        if (current && ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE'].includes(current.tagName)) {
            this.stack.pop();
        }
        if(closeAllBlocks) {
            this.stack = [];
        }

    }

    private closeTable(): void {
        let current = this.getCurrentElement();
        while (current && ['TBODY', 'THEAD', 'TR', 'TD'].includes(current.tagName)) {
            this.popElement();
            current = this.getCurrentElement();
        }
        if (current && current.tagName === 'TABLE') {
            this.popElement();
        }
        this.isInTable = false;
        this.isParsingTableContent = false;
    }

    lastListDepth=-1;
    private ensureList(type: 'ul' | 'ol', indentLevel:number): void {
        const current = this.getCurrentElement();
        // const parent = this.stack.length > 1 ? this.stack[this.stack.length - 2] : null;

        if(indentLevel<this.lastListDepth) {
            for(let i=this.lastListDepth; i>indentLevel; i--) {
                // console.log("close list for indentLevel", indentLevel, "lastListDepth", this.lastListDepth);
                this.stack.pop();
            }
        }
        this.lastListDepth=indentLevel;

        if (current && current.tagName === 'LI') {
            // If we're already inside a list item, we don't need to do anything.
            // console.log("Already in a list item, no action taken.");
            return;
        }

        let hasList=true;
        // console.log("is length enough", this.stack.length, "to", indentLevel+1);
        if(this.stack.length>=indentLevel+1) {
            // console.log("check stack from", this.stack.length, "to", this.stack.length-indentLevel-1);
            for (let i = this.stack.length; i >= this.stack.length-indentLevel; i--) {
                if (this.stack[i-1].tagName !== "UL" && this.stack[i-1].tagName !== "OL") {
                    hasList=false;
                    break;
                }
            }
        }else{
            hasList=false;
        }
        if(!hasList){
            this.closeCurrentBlock(false);
            this.pushElement(type);
        }
        if (current && current.tagName !== type.toUpperCase()) {
            // This handles switching between ordered and unordered lists
            this.closeCurrentBlock(false);
            this.pushElement(type);
        }
    }
}